import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { apiService } from '../Config/api.service';
import { canSuperviserDemandesRh } from '../Auth/permissions';

// Un compteur n'a pas besoin d'être relu en continu : au changement de page,
// au retour sur la fenêtre, après une décision, et toutes les deux minutes
// pour ce qui arrive pendant qu'on lit.
const INTERVALLE_RELECTURE = 120000;

// Sans fournisseur — un écran monté seul, dans un test —, pas de compteurs,
// et signaler une décision ne fait rien.
const ATraiterContexte = createContext({ compteurs: null, relire: () => {} });

/**
 * Les demandes en attente de la société active, comptées par type
 * (Yros/Kavero#416). Une seule lecture pour la boîte « À traiter » et les
 * pastilles du menu.
 *
 * Seul un compte qui supervise les demandes les lit : la route est réservée
 * côté proxy, et un refus d'accès renverrait l'écran vers la page 403.
 */
export const ATraiterProvider = ({ actif, user, children }) => {
  const autorise = Boolean(actif && user && canSuperviserDemandesRh(user));
  const { pathname } = useLocation();
  const [compteurs, setCompteurs] = useState(null);
  const derniereLecture = useRef(0);

  const relire = useCallback(async () => {
    if (!autorise) return;
    const numero = ++derniereLecture.current;
    try {
      const reponse = await apiService.compterATraiter();
      // Deux lectures peuvent se croiser : seule la dernière compte.
      if (numero !== derniereLecture.current) return;
      // `supervision` : ce que liste « Demandes à traiter ». Les déclarations
      // d'heures se décident dans « Feuilles de temps » (Yros/Kavero#220), le
      // total de la boîte les garde.
      setCompteurs(reponse?.ok
        ? {
          ...reponse.a_traiter,
          total: reponse.total,
          supervision: reponse.total - (reponse.a_traiter?.heures_sup || 0),
        }
        : null);
    } catch (e) {
      // Un compteur indisponible se tait : il ne bloque rien.
    }
  }, [autorise]);

  useEffect(() => {
    if (!autorise) {
      setCompteurs(null);
      return;
    }
    relire();
  }, [autorise, pathname, relire]);

  useEffect(() => {
    if (!autorise) return undefined;
    const minuterie = setInterval(relire, INTERVALLE_RELECTURE);
    window.addEventListener('focus', relire);
    return () => {
      clearInterval(minuterie);
      window.removeEventListener('focus', relire);
    };
  }, [autorise, relire]);

  const valeur = useMemo(() => ({ compteurs, relire }), [compteurs, relire]);
  return <ATraiterContexte.Provider value={valeur}>{children}</ATraiterContexte.Provider>;
};

/**
 * `compteurs` : `{conge, avance, acompte, pret, note_frais, heures_sup, total,
 * supervision}`, ou `null`
 * quand il n'y a rien à montrer. `relire` : à appeler après une décision.
 */
export const useATraiter = () => useContext(ATraiterContexte);
